import moment from 'moment';
import {
  IncidentAnalyticsFilterParams,
  IncidentAnalyticsFilterResponseAssignee,
  IncidentAnalyticsFilterResponseService,
  IncidentAnalyticsFilterResponseTag,
  NoiseReductionByServiceMetric,
  NoiseReductionByTeamMetric,
  Interval,
  OnCallUsersPerHourParams,
} from '../api/types';
import { DonutProps } from '../components/Charts/Donut/types';
import { FilterFormProps } from '../components/IncidentAnalytics/components/filter-drawer/type';
import {
  NOISE_REDUCTION_COLORS,
  NOISE_REDUCTION_LABELS,
  RESPONSE_METRICS_OPTIONS,
} from '../constants';
import {
  DateRange,
  IncidentAnalyticsFilterForm,
  OnCallUsersAnalyticsFilterForm,
  PaginationType,
} from '../hooks/types';
import momentTimeZone from 'moment-timezone';

import { useAnalyticsV3Context } from '../hooks/useAnalyticsV3Context';
import { useNoiseReductionTabContext } from '../hooks/useNoiseReductionTabContext';
import { matchPath } from 'react-router-dom';
import { ANALYTICS_V3_PATH } from 'views/main/routes/routes';
export type Units = [display: string, tooltip: string];
export type UnitsFn = (value: number) => Units;

export const getTagMap = (
  tags: IncidentAnalyticsFilterResponseTag[],
): FilterFormProps['tagMap'] => {
  return tags.reduce((acc, tag) => {
    acc.set(tag.key, tag.values);
    return acc;
  }, new Map<string, string[]>());
};

export const getServicesList = (services: IncidentAnalyticsFilterResponseService[]) => {
  return services.map(service => ({ label: service.name, value: service.id }));
};

export const getServiceOwnersMap = (serviceOwnerMap: { [key: string]: string[] }) => {
  return Object.keys(serviceOwnerMap).map(key => ({ label: key, value: key }));
};

export const getPriorityList = (priority: string[]) => {
  return priority.map(key => ({ label: key, value: key }));
};

export const getCurrentAssigneeList = (
  currentAssignee: IncidentAnalyticsFilterResponseAssignee,
) => {
  return currentAssignee.squads.map(squad => ({ label: squad.name, value: squad.id }));
};

export const transformFiltersForAPI = (
  filters: IncidentAnalyticsFilterForm,
  aggregateBy?: typeof RESPONSE_METRICS_OPTIONS[number],
): Pick<
  IncidentAnalyticsFilterParams,
  'assignee' | 'service' | 'priority' | 'tags' | 'serviceOwner'
> => {
  const { assignee, service, tagKey, tagValue, priority, serviceOwner } = filters;

  const assigneeList = [
    ...assignee.users,
    ...assignee.squads,
    ...assignee.myUsers,
    ...assignee.mySquads,
  ]
    .map(assignee => assignee.value)
    .filter(Boolean);

  const serviceOwnerList = [
    ...serviceOwner.users,
    ...serviceOwner.squads,
    ...serviceOwner.myUsers,
    ...serviceOwner.mySquads,
  ]
    .map(serviceOwner => serviceOwner.value)
    .filter(Boolean);

  const serviceList = service.map(service => service.value);
  const tagValueList = tagValue.map(value => value.value);
  const priorityList = priority.map(priority => priority.value);

  const filtersForAPI = {
    service: serviceList,
    serviceOwner: serviceOwnerList,
    priority: priorityList,
    assignee: assigneeList,
    tags: tagKey?.value ? tagValueList.map(value => `${tagKey?.value}:${value}`) : undefined,

    aggregateBy,
  };

  return filtersForAPI;
};

export const transformOnCallFiltersForAPI = (
  filters: OnCallUsersAnalyticsFilterForm,
): Pick<OnCallUsersPerHourParams, 'schedule_owners' | 'schedule_ids' | 'search'> => {
  const { schedule, scheduleOwner, searchTerm } = filters;

  const scheduleOwnerList = [
    ...scheduleOwner.users,
    ...scheduleOwner.squads,
    ...scheduleOwner.myUsers,
    ...scheduleOwner.mySquads,
  ]
    .map(scheduleOwner => scheduleOwner.value)
    .filter(Boolean);
  const scheduleList = schedule.map(schedule => Number(schedule.value));

  const filtersForAPI: Pick<
    OnCallUsersPerHourParams,
    'schedule_owners' | 'schedule_ids' | 'search'
  > = {
    schedule_ids: scheduleList,
    schedule_owners: scheduleOwnerList,
    search: filters.searchTerm,
  };

  return filtersForAPI;
};

export const getSortByFromSortData = (pagination: PaginationType, defaultId: string) => {
  const { sortBy } = pagination;
  const id = sortBy?.[0]?.id ?? defaultId;
  const desc = sortBy?.[0]?.desc ?? false;

  if (!desc) {
    return `+${id}`;
  }
  return `-${id}`;
};

export const getParamsForAPI = (filters: IncidentAnalyticsFilterParams) => {
  const {
    assignee,
    service,
    tags,
    priority,
    startDate,
    endDate,
    aggregateBy,
    pageNumber,
    pageSize,
    sortBy,
    serviceOwner,
  } = filters;

  const params = {
    teamID: filters.teamID,
    startDate: startDate ?? '2024-12-01T00:00:00.00Z',
    endDate: endDate ?? '2024-12-17T00:00:00.00Z',
    service: service,
    assignee: assignee,
    priority: priority,
    tags: tags,
    aggregationType: aggregateBy,
    pageNumber: pageNumber === undefined || pageNumber === null ? undefined : pageNumber + 1,
    pageSize: pageSize === undefined || pageSize === null ? undefined : pageSize,
    sortBy: sortBy,
    serviceOwner: serviceOwner,
  };
  return params;
};

export type OnCallUserFilters = {
  OwnerId: string;
  Search?: string;
  SortBy?: string;
  StartTime: string;
  EndTime: string;
  PageNumber?: number;
  PageSize?: number;
  ScheduleIds?: number[];
  EntityOwner?: string;
};

// Function to map PascalCase filters to snake_case API parameters
export const getOnCallAPIParams = (filters: OnCallUsersPerHourParams): OnCallUsersPerHourParams => {
  const {
    owner_id,
    search,
    start_time,
    end_time,
    page_number = 0, // Default pagination starts from 0
    page_size = 5, // Default page size
    schedule_ids,
    schedule_owners,
  } = filters;

  return {
    owner_id, // Required: Unique team identifier
    search, // Optional: Search filter (e.g., user name, role)
    start_time: start_time ?? '2024-01-01T00:00:00Z', // Default start time
    end_time: end_time ?? '2024-01-31T23:59:59Z', // Default end time
    page_number: page_number + 1, // Convert to 1-based pagination
    page_size, // Number of results per page
    schedule_ids, // Optional: Array of schedule IDs
    schedule_owners, // Optional: Entity owner filter
  };
};

/**
 * take unit of time in seconds and covert to human format
 * @param  {number} - time in seconds
 * @returns {string} - time in human format
 * uses vanila javascript date object
 */
const ONE_MINUTE = 60;
const ONE_HOUR = 3600;
const ONE_DAY = 86400;
const ONE_WEEK = 604800;
const ONE_MONTH = 2629746;
const ONE_YEAR = 31556952;

export const humanizeDuration = (time: number, precision = 2) => {
  if (time < ONE_MINUTE) {
    return `${time.toFixed(precision)} second${time === 1 ? '' : 's'}`;
  }
  if (time < ONE_HOUR) {
    const val = time / ONE_MINUTE;
    return `${val.toFixed(precision)} minute${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_DAY) {
    const val = time / ONE_HOUR;
    return `${val.toFixed(precision)} hour${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_WEEK) {
    const val = time / ONE_DAY;
    return `${val.toFixed(precision)} day${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_MONTH) {
    const val = time / ONE_WEEK;
    return `${val.toFixed(precision)} week${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_YEAR) {
    const val = time / ONE_MONTH;
    return `${val.toFixed(precision)} month${val === 1 ? '' : 's'}`;
  }
  const val = time / ONE_YEAR;
  return `${val.toFixed(precision)} year${val === 1 ? '' : 's'}`;
};

export const humanizeDurationForChart = (time: number, precision = 2) => {
  if (time < ONE_MINUTE) {
    return `${time.toFixed(precision)} sec${time === 1 ? '' : 's'}`;
  }
  if (time < ONE_HOUR) {
    const val = time / ONE_MINUTE;
    return `${val.toFixed(precision)} min${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_DAY) {
    const val = time / ONE_HOUR;
    return `${val.toFixed(precision)} hr${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_WEEK) {
    const val = time / ONE_DAY;
    return `${val.toFixed(precision)} day${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_MONTH) {
    const val = time / ONE_WEEK;
    return `${val.toFixed(precision)} wk${val === 1 ? '' : 's'}`;
  }
  if (time < ONE_YEAR) {
    const val = time / ONE_MONTH;
    return `${val.toFixed(precision)} mn${val === 1 ? '' : 's'}`;
  }
  const val = time / ONE_YEAR;
  return `${val.toFixed(precision)} yr${val === 1 ? '' : 's'}`;
};

export const generateDonutDataForNoiseReduction = (
  data: NoiseReductionByServiceMetric,
): DonutProps['data'] => {
  return data.countByReductionType?.map(item => ({
    name: NOISE_REDUCTION_LABELS[item.reductionType],
    value: item.count,
    color: NOISE_REDUCTION_COLORS[item.reductionType],
  }));
  // .concat({
  //   name: NOISE_REDUCTION_LABELS.Triggered,
  //   value: data.triggeredCount,
  //   color: NOISE_REDUCTION_COLORS.Triggered,
  // }) ?? []
};

export const generateDonutDataForNoiseReductionTeam = (
  data: NoiseReductionByTeamMetric,
): DonutProps['data'] => {
  return data.countByReductionType?.map(item => ({
    name: NOISE_REDUCTION_LABELS[item.reductionType],
    value: item.count,
    color: NOISE_REDUCTION_COLORS[item.reductionType],
  }));
};

export const getDateWithTimeZoneOffset = (date: Date, timeZone: string) => {
  // Create moment object in target timezone using local date components
  const m = momentTimeZone.tz(
    {
      year: date.getFullYear(),
      month: date.getMonth(),
      date: date.getDate(),
      hours: date.getHours(),
      minutes: date.getMinutes(),
      seconds: date.getSeconds(),
      milliseconds: date.getMilliseconds(),
    },
    timeZone,
  );

  // Convert to UTC while preserving the local time values
  return m.utc().toDate();
};

export const prepareDateRangeForAPI = (dateRange: DateRange) => {
  const { startDate, endDate, timeZone } = dateRange;

  const endDateIncremented = moment(endDate)
    .set('hour', 23)
    .set('minute', 59)
    .set('second', 59)
    .set('millisecond', 999)
    .toDate();

  const startDateWithTimeZoneOffset = getDateWithTimeZoneOffset(startDate, timeZone);
  const endDateWithTimeZoneOffset = getDateWithTimeZoneOffset(endDateIncremented, timeZone);

  return {
    startDate: startDateWithTimeZoneOffset.toISOString(),
    endDate: endDateWithTimeZoneOffset.toISOString(),
    timeZone,
  };
};

const intervalToFormatMap: Record<Interval, string> = {
  hour: 'DD MMM YYYY HH:mm',
  day: 'DD MMM YYYY',
  month: 'MMM YYYY',
  week: 'DD MMM YYYY',
};

export const formatDateForChart = (date: string | Date, interval: Interval, timeZone: string) => {
  const m = momentTimeZone.tz(date, timeZone);
  return m.format(intervalToFormatMap[interval]);
};

export const resetAnalyticsV3Filters = () => {
  useAnalyticsV3Context.getState().resetFilters();
};

export const resetAnalyticsV3Pagination = () => {
  useNoiseReductionTabContext.getState().resetAPTAPagination();
};

export const resetAnalyticsV3FiltersAndPagination = () => {
  const isAnalyticsRoute = matchPath(window.location.pathname, {
    path: ANALYTICS_V3_PATH,
  });

  const isOrgAnalyticsRoute = matchPath(window.location.pathname, {
    path: '/settings/org-analytics',
  });

  if (isAnalyticsRoute || isOrgAnalyticsRoute) {
    resetAnalyticsV3Filters();
    resetAnalyticsV3Pagination();
  }
};
